import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, Form, GetRef, Input, InputRef, Select, Table } from 'antd'
import type { TableColumnsType, TableProps } from 'antd'
import { useContext, useMemo } from 'react'
import { HolderOutlined } from '@ant-design/icons'
import type { DragEndEvent } from '@dnd-kit/core'
import { DndContext } from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { isWindow, switchACMG } from '@/@core/utils/utils'
import TableDetails from '../tanstack-tables/table-details/TableDetails'
import { Box, Grid } from '@mui/material'

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void
  listeners?: SyntheticListenerMap
}

const RowContext = React.createContext<RowContextProps>({})

type FormInstance<T> = GetRef<typeof Form<T>>

const EditableContext = React.createContext<FormInstance<any> | null>(null)

type TableRowSelection<T> = TableProps<T>['rowSelection']

interface EditableRowProps {
  index: number
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string
  index: number
}

const color = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgb(254,130,138)'
  },
  {
    key: 'likely pathogenic',
    value: 'likelyPathogenic',
    color: 'rgb(254,196,210)'
  },
  {
    key: 'drug response',
    value: 'drugResponse',
    color: 'rgb(186,204,147)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertainSignificance',
    color: 'rgb(240,239,240)'
  },
  {
    key: 'likely benign',
    value: 'likelyBenign',
    color: 'rgb(134,169,209)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(103,142,197)'
  },
  {
    key: 'loss of function',
    value: 'lossOfFunction',
    color: 'rgb(246,209,139)'
  }
]

export const DragHandle: React.FC = () => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext)

  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  )
}

const Row: React.FC<RowProps> = (props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    index
  } = useSortable({ id: props['data-row-key'] })
  const [form] = Form.useForm()

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 1000 } : {})
  }

  const contextValue = useMemo<RowContextProps>(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners]
  )

  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <RowContext.Provider value={contextValue}>
          <tr {...props} ref={setNodeRef} style={style} {...attributes} />
        </RowContext.Provider>
      </EditableContext.Provider>
    </Form>
  )
}

interface EditableCellProps {
  title: React.ReactNode
  editable: boolean
  editType: 'input' | 'select'
  valueOptions?: { label: string; value: string }[]
  dataIndex: any
  record: any
  handleSave: (record: any) => void
}

interface Props {
  columns: (ColumnTypes[number] & {
    editable?: boolean
    dataIndex: string
    valueOptions?: { label: string; value: string }[]
    editType?: 'input' | 'select'
  })[]
  rows: any[] | []
  rowCount?: number
  mainHeight?: number
  paginationModel?: {
    page: number
    pageSize: number
  }
  paginationMode?: 'client' | 'server'
  onPaginationModelChange?: any
  onFilterModelChange?: any
  onSortModelChange?: any
  setPagination?: any
  onRowSelectionModelChange?: any
  onCellClick?: any
  onCellEditStop?: any
  onCellEditStart?: any
  processRowUpdate?: any
  onProcessRowUpdateError?: any
  autoPageSize?: boolean
  initialState?: any
  rowSelectionModel?: Array<number | string> | number | string
  rowHeight?: number
  disableRowSelectionOnClick?: boolean
  disableRowSelection?: boolean
  checkboxSelection?: boolean
  isColorToggle?: boolean
  sx?: object
  loading?: boolean
  slots?: any
  pageSizeOptions?: (
    | number
    | {
        value: number
        label: string
      }
  )[]
  disableColumnMenu?: boolean
  disableVirtualization?: boolean
  sortingMode?: 'client' | 'server'
  analysisInfo?: any
  isFetching: boolean
  setPage?: any
  variantCount?: number
  isExpanded?: boolean
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  title,
  editable,
  editType,
  valueOptions,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false)
  const inputRef = React.useRef<InputRef | any>(null)
  const form = React.useContext(EditableContext)!

  React.useEffect(() => {
    if (editing) {
      inputRef.current?.focus()
    }
  }, [editing])

  const toggleEdit = () => {
    setEditing(!editing)

    form.setFieldsValue({ [dataIndex]: record[dataIndex] })
  }

  const save = async () => {
    try {
      const values = await form.validateFields()

      toggleEdit()

      //

      if (values && values.type) {
        handleSave({ ...record, ...values, variantsType: values.type })
      } else handleSave({ ...record, ...values })
    } catch (errInfo) {
      //
    }
  }

  let childNode = children

  if (editable) {
    if (editing) {
      if (editType === 'select')
        childNode = (
          <Form.Item
            style={{ margin: 0 }}
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `${title} is required.`
              }
            ]}
          >
            <Select
              style={{ width: 100 }}
              onChange={save}
              options={valueOptions}
              ref={inputRef}
              onBlur={(e) => {
                //

                toggleEdit()
              }}
            />
          </Form.Item>
        )
      else
        childNode = (
          <Form.Item
            style={{ margin: 0 }}
            name={dataIndex}
            rules={[
              {
                required: true,
                message: `${title} is required.`
              }
            ]}
          >
            <Input ref={inputRef} onPressEnter={save} onBlur={save} />
          </Form.Item>
        )
    } else
      childNode = (
        <div className="editable-cell-value-wrap" onClick={toggleEdit}>
          {children}
        </div>
      )
  }

  return <td {...restProps}>{childNode}</td>
}

type EditableTableProps = Parameters<typeof Table>[0]

export type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const VariantsAntdTables = React.forwardRef((props: Props, ref) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)

  const tableRef = React.useRef(null)
  const tableConRef = React.useRef(null)
  const [tableMaxHeight, setTableMaxHeight] = React.useState<number>(500)
  const [tableMaxWeight, setTableMaxWeight] = React.useState<number>(2000)

  const [dataSource, setDataSource] = React.useState<any[]>(props.rows)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const pageSize = React.useMemo(() => {
    return props.initialState?.pagination?.paginationModel?.pageSize ?? 15
  }, [props.initialState])

  const getTableMaxHight = () => {
    const backdrop = document.getElementById('backdrop') as HTMLElement
    const header1 = document.getElementsByTagName('header')[0]
    const header2 = document.getElementById('header2') as HTMLElement
    const header3 = document.getElementById('header3') as HTMLElement
    const header4 = document.getElementById('header4') as HTMLElement
    const main = document.getElementsByTagName('main')[0]

    setTableMaxWeight(backdrop.clientWidth - 100)

    if (props.rowCount) {
      setTableMaxHeight(
        Number(
          `${Math.floor(
            backdrop?.scrollHeight -
              header1.scrollHeight -
              header2?.scrollHeight -
              header3?.scrollHeight -
              (isWindow() ? (header4 ? 240 : 160) : 100)
          )}`
        )
      )
    } else setTableMaxHeight(500)

    return `${Math.floor(
      (1 -
        header1.scrollHeight / window.screen.height -
        header2?.scrollHeight / window.screen.height -
        header3?.scrollHeight / window.screen.height) *
        100
    )}vh`
  }

  const throttle = (fn: () => void, delay: number | undefined) => {
    let timer = null as any

    return function () {
      clearTimeout(timer)

      timer = setTimeout(() => {
        fn()
      }, delay)
    }
  }

  React.useEffect(() => {
    getTableMaxHight()
  })

  React.useEffect(() => {
    setDataSource(
      props.rows.map((item, index) => {
        if (
          item.id &&
          typeof item.id === 'object' &&
          Object.keys(item.id).length
        )
          return item
        else return { ...item, id: index }
      })
    )
  }, [props.rows])

  React.useEffect(() => {
    let lastPixelRatio = window.devicePixelRatio

    window.addEventListener('resize', function () {
      const currentPixelRatio = window.devicePixelRatio

      if (currentPixelRatio !== lastPixelRatio) {
        throttle(getTableMaxHight, 500)
      }

      lastPixelRatio = currentPixelRatio
    })
  })

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setDataSource((prevState) => {
        const activeIndex = prevState.findIndex(
          (record) => record.key === active?.id
        )
        const overIndex = prevState.findIndex(
          (record) => record.key === over?.id
        )

        return arrayMove(prevState, activeIndex, overIndex)
      })
    }
  }
  const rowSelection = {
    preserveSelectedRowKeys: true,
    onChange: (selectedRowKey: React.Key[], selectedRows: object[]) => {
      setSelectedRowKeys([...new Set([...selectedRowKey])])

      props.onRowSelectionModelChange(selectedRowKey, selectedRows)
    },
    columnWidth: 48,
    selectedRowKeys
  }

  const handleSave = (row: any) => {
    const newData = [...props.rows]
    const index = newData.findIndex((item) => row.key === item.key)
    const item = newData[index]

    newData.splice(index, 1, {
      ...item,
      ...row
    })

    //

    props.processRowUpdate && props.processRowUpdate(row, newData)
  }

  const components = {
    body: {
      row: Row,
      cell: EditableCell
    }
  }

  const handleScroll = () => {
    const tableCons = document.getElementsByClassName('ant-table-body')
    const tableCons2 = document.getElementsByClassName(
      'ant-table-tbody-virtual-holder'
    )
    if (tableCons2 && tableCons2.length) {
      const tableCon = tableCons2[0]

      // const tableCon2 = tableCons2[0]

      const handleScroll = () => {
        const scrollTop = tableCon.scrollTop
        const clientHeight = tableCon.clientHeight
        const scrollHeight = tableCon.scrollHeight

        const totalDBRowCount = props.rowCount ?? 0
        const totalFetched = props.rows.length

        if (
          scrollHeight - scrollTop - clientHeight < 900 &&
          !props.isFetching &&
          totalFetched < totalDBRowCount
        ) {
          props.setPage && props.setPage()
        }
      }

      handleScroll()
    }
  }

  const columns = props.columns.map((col) => {
    if (!col.editable) {
      return col
    }

    return {
      ...col,
      onCell: (record: any) => ({
        record,
        editable: col.editable,
        editType: col.editType,
        valueOptions: col.valueOptions,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave
      })
    }
  })

  const rowClassName = (record: any, index: any) => {
    let bgcolor = color.find(
      (i) => i.key === record.CLINSIG_final?.toLowerCase()
    )
      ? (color.find((i) => i.key === record.CLINSIG_final?.toLowerCase())
          ?.value as any)
      : 'white'

    // loss_of_function等于1 并且CLINSIG_final不等于P/LP
    if (record.CLINSIG_final) {
      if (record.CLINSIG_final?.toLowerCase() === 'pathogenic') {
        //
      } else if (record.CLINSIG_final?.toLowerCase() === 'likely pathogenic') {
        //
      } else if (record.loss_of_function === 1 || record.is_loss_of_function)
        bgcolor = 'lossOfFunction'
    } else if (record.clinvar_clinsig_final || record.gls_classification) {
      if (
        record.clinvar_clinsig_final?.includes('PAT') ||
        record.clinvar_clinsig_final?.includes('LP')
      ) {
        if (record.gls_classification) {
          bgcolor = color.find(
            (i) => i.key === switchACMG(record.gls_classification)
          )
            ? (color.find(
                (i) => i.key === switchACMG(record.gls_classification)
              )?.value as any)
            : 'white'
        }
      } else {
        if (record.gls_classification) {
          bgcolor = color.find(
            (i) => i.key === switchACMG(record.gls_classification)
          )
            ? (color.find(
                (i) => i.key === switchACMG(record.gls_classification)
              )?.value as any)
            : 'white'
        }
        if (record.is_loss_of_function) bgcolor = 'lossOfFunction'
      }
    } else if (record.ccnv_classification) {
      bgcolor = color.find(
        (i) => i.key === record.ccnv_classification?.toLowerCase()
      )
        ? (color.find(
            (i) => i.key === record.ccnv_classification?.toLowerCase()
          )?.value as any)
        : 'white'
    }

    return bgcolor
  }

  React.useImperativeHandle(ref, () => {
    return {}
  })

  return (
    <div style={{ width: '100%', overflow: 'auto' }}>
      <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={dataSource.map((i) => i.key)}
          strategy={verticalListSortingStrategy}
        >
          <Box ref={tableRef}>
            <Table
              rowSelection={
                (props.disableRowSelection
                  ? undefined
                  : {
                      ...rowSelection
                    }) as any
              }
              rowHoverable={false}
              columns={columns as ColumnTypes}
              dataSource={dataSource.map((item: any) => {
                item.key = item.AnnotSV_ID ? item.AnnotSV_ID : item.id

                return item
              })}
              components={components}
              size="middle"
              onChange={(pagination, filters, sorter) => {
                //

                props.onPaginationModelChange &&
                  props.onPaginationModelChange({
                    page: pagination.current as number,
                    pageSize: pagination.pageSize
                  })

                props.onFilterModelChange && props.onFilterModelChange(filters)

                props.onSortModelChange && props.onSortModelChange(sorter)
              }}
              pagination={{
                pageSize: 30,
                total: props.rowCount,
                showSizeChanger: false,
                showTotal: (total, range) => {
                  return `${range[0]}-${range[1]} ${t('of')} ${total} ${t(
                    'variance'
                  )}`
                },
                position: ['none', 'bottomCenter']
              }}
              expandable={
                props.isExpanded
                  ? {
                      expandedRowRender: (record) => (
                        <TableDetails
                          params={record}
                          analysisInfo={props.analysisInfo}
                        />
                      ),
                      columnWidth: 48
                    }
                  : undefined
              }
              scroll={{
                y: tableMaxHeight,
                x: tableMaxWeight
              }}
              virtual={false}
              rowKey="id"
              onScroll={(e) => {
                e.stopPropagation()

                // handleScroll()
              }}
              ref={tableConRef}
              rowClassName={props.isColorToggle ? undefined : rowClassName}
            />

            <div className="h-2" />

            {/* <Grid
              container
              className="flex items-center gap-2"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1
              }}
            >
              <Grid item xs>
                <span
                  className="flex items-center gap-1"
                  style={{
                    alignItems: 'center',
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  {`${t('Display')}${props.rows.length}${
                    props.rowCount
                      ? `，${t('a total of')} ${props.rowCount} ${t(
                          'records'
                        )} - ${props.variantCount} ${t('variance')} - ${
                          props.rows.length === props.rowCount
                            ? t('Data loading completed')
                            : !props.isFetching
                            ? t('Slide loading more')
                            : t('Please wait for a moment')
                        }`
                      : ''
                  }`}
                </span>
              </Grid>
            </Grid> */}
          </Box>
        </SortableContext>
      </DndContext>
    </div>
  )
})
export default VariantsAntdTables
